import { GridColDef, GridFilterModel } from "@mui/x-data-grid";
import CustomDataGrid from "../../components/ui/customDataGrid/customDataGrid.component";
import { useTranslation } from "react-i18next";
import { Handshake, Print, CheckCircle, RequestQuote, Email } from "@mui/icons-material";
import { CONTRACTS_ENDPOINT } from "../../consts";
import { useNavigate, useLocation } from "react-router-dom";
import { Chip, IconButton, Tooltip } from "@mui/material";
import { useMemo, useState } from "react";
import { useHasPermission } from "../../contexts/auth";
import EntityReferenceLink from "../../components/ui/entityReference/entityReferenceLink.component";
import { ContractEndDateDisplay, formatContractEndDate } from "../../utils";
import CustomModal from "../../components/ui/modal/modal.component";
import { SendReminder } from "../../components/settlements/sendReminder/sendReminder.component";
import {
  containsStringFilterOperators,
  dateRangeFilterOperators,
  equalsNumberFilterOperators,
} from "../../components/ui/customDataGrid/filterOperators";

export default function ContractsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const canViewClients = useHasPermission("settlement.view_client");
  const canViewContracts = useHasPermission("settlement.view_contract");
  const canChangeSettlement = useHasPermission("settlement.change_settlement");
  const [sendReminderContractId, setSendReminderContractId] = useState<string | null>(null);

  const initialSortModel = (() => {
    try {
      const params = new URLSearchParams(location.search);
      const ordering = params.get("ordering");
      if (!ordering) return undefined;
      return ordering.split(",").map((o) => ({ field: o.replace(/^-/, ""), sort: o.startsWith("-") ? ("desc" as const) : ("asc" as const) }));
    } catch (e) {
      return undefined;
    }
  })();

  // Memoize on the param value, not the whole location.search: CustomDataGrid
  // mirrors the current page into the URL, and it resets to page 0 whenever it
  // receives a new initialFilterModel reference - keying on location.search
  // would snap every "next page" click straight back to the first page.
  const endedParam = new URLSearchParams(location.search).get("ended");
  const initialFilterModel: GridFilterModel | undefined = useMemo(() => {
    if (endedParam !== "true" && endedParam !== "false") return undefined;
    return { items: [{ id: "ended-filter", field: "ended", operator: "is", value: endedParam === "true" }] };
  }, [endedParam]);

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      filterOperators: equalsNumberFilterOperators(),
    },
    {
      field: "number",
      headerName: t("Number"),
      filterOperators: containsStringFilterOperators(),
    },
    {
      field: "internal_name",
      headerName: t("Internal name"),
      filterOperators: containsStringFilterOperators(),
    },
    {
      field: "settlement_kinds",
      headerName: t("Settlement kind"),
      filterable: false,
      valueGetter: (value, row) => row.settlement_kinds?.join(", ") || "-",
    },
    {
      field: "client__name",
      headerName: t("Client"),
      editable: false,
      filterOperators: containsStringFilterOperators(),
      valueGetter: (value, row) => row.client?.name,
      renderCell: (params) => (
        <EntityReferenceLink
          entityType="client"
          entityId={params.row.client?.id}
          label={params.row.client?.name || "-"}
          canPreview={canViewClients}
        />
      ),
    },
    {
      field: "equipment_quantity",
      headerName: t("Equipments"),
      editable: false,
      filterable: false,
      renderHeader: () => (
        <Tooltip title={t("Equipments")}>
          <Print fontSize="small" />
        </Tooltip>
      ),
    },
    {
      field: "settlements_count",
      headerName: t("Ready to settle"),
      editable: false,
      filterable: false,
      renderHeader: () => (
        <Tooltip title={t("Ready to settle")}>
          <CheckCircle fontSize="small" />
        </Tooltip>
      ),
      renderCell: (params) => {
        const readyToSettle = Number(params.row.settlements_ready_to_settle_count || 0);
        const allSettlements = Number(params.row.settlements_count || 0);
        const ratio = allSettlements > 0 ? Math.min(readyToSettle / allSettlements, 1) : 0;

        return (
          <Chip
            label={`${readyToSettle}/${allSettlements}`}
            size="small"
            sx={(theme) => {
              const isDarkMode = theme.palette.mode === "dark";
              const saturation = isDarkMode ? 45 + ratio * 35 : 35 + ratio * 45;
              const lightness = isDarkMode ? 24 + ratio * 18 : 88 - ratio * 40;
              const background = `hsl(140 ${saturation}% ${lightness}%)`;

              return {
                bgcolor: background,
                color: theme.palette.getContrastText(background),
                fontWeight: 700,
                border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.18)" : "1px solid rgba(0, 0, 0, 0.08)",
                "& .MuiChip-label": {
                  color: "inherit",
                },
              };
            }}
          />
        );
      },
    },
    {
      field: "has_pending_settlements",
      headerName: t("Has pending settlements"),
      editable: false,
      filterable: false,
      renderHeader: () => (
        <Tooltip title={t("Has pending settlements")}>
          <RequestQuote fontSize="small" />
        </Tooltip>
      ),
      valueGetter: (value, row) => (row.has_pending_settlements ? 1 : 0),
      renderCell: (params) => {
        const hasPending = Boolean(params.row.has_pending_settlements);
        return (
          <Chip
            label={hasPending ? t("Unsettled") : t("Settled")}
            size="small"
            sx={{
              bgcolor: hasPending ? "warning.light" : "success.light",
              color: hasPending ? "warning.contrastText" : "success.contrastText",
              fontWeight: 600,
              "& .MuiChip-label": {
                color: hasPending ? "warning.contrastText" : "success.contrastText",
              },
            }}
          />
        );
      },
    },
    {
      field: "start_date",
      headerName: t("Start Date"),
      filterOperators: dateRangeFilterOperators(),
    },
    {
      field: "end_date",
      headerName: t("End Date"),
      filterable: false,
      valueGetter: (value, row) =>
        formatContractEndDate(row.end_date, row.indefinite_after_end_date, t),
      renderCell: (params) => (
        <ContractEndDateDisplay
          endDate={params.row.end_date}
          indefiniteAfterEndDate={params.row.indefinite_after_end_date}
          t={t}
        />
      ),
    },
    {
      // Not shown (see defaultHiddenFields below) - exists only so the "ended"
      // quick filter targets a real column. Without a matching column, MUI
      // DataGrid's own columnsChange handling treats the filter item as
      // referencing an unknown field and silently strips it back out via
      // onFilterModelChange, undoing the quick filter right after it's applied.
      field: "ended",
      headerName: t("Ended"),
      type: "boolean",
    },
    {
      field: "actions",
      headerName: t("Actions"),
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title={t("Send reminder")}>
          <span>
            <IconButton
              size="small"
              disabled={!canChangeSettlement}
              onClick={(event) => {
                event.stopPropagation();
                setSendReminderContractId(String(params.row.id));
              }}
            >
              <Email fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  const handleRowClick = (id: string) => {
    navigate(`/contracts/${id}`);
  };

  if (!canViewContracts) {
    return null;
  }

  return (
    <>
      <CustomDataGrid
        icon={<Handshake />}
        title={t("Contracts list")}
        url={CONTRACTS_ENDPOINT}
        gridSettingsKey="CONTRACTS"
        columns={columns}
        initialSortModel={initialSortModel}
        initialFilterModel={initialFilterModel}
        defaultHiddenFields={["ended"]}
        refreshEventName="contractAdded"
        onRowClick={handleRowClick}
        dataGridProps={{
          getRowClassName: (params) => (params.row.ended ? "contract-row-ended" : ""),
          sx: {
            "& .contract-row-ended": {
              opacity: 0.55,
            },
          },
        }}
      />
      <CustomModal
        opened={sendReminderContractId !== null}
        setOpened={(value) => {
          if (!value) {
            setSendReminderContractId(null);
          }
        }}
        title={t("Send reminder")}
        content={
          <SendReminder
            preselectedContractId={sendReminderContractId ?? undefined}
            onSuccess={() => {
              setSendReminderContractId(null);
              window.dispatchEvent(new Event("settlementUpdated"));
            }}
          />
        }
      />
    </>
  );
}
